"use client"
import { useEffect } from "react"
import { sendClientError } from "@/lib/errors/client-report"

// global-error.tsx REPLACES the root layout when the root layout (or anything
// it renders) throws — so error.tsx, which only wraps a route segment, can't
// catch that case. This must render its own <html>/<body> and avoid next/link
// or layout CSS, since the shell itself is what failed.
const reported = new Set<string>()

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    const key = (error.digest ?? "") + "|" + (error.message ?? "")
    if (reported.has(key)) return
    reported.add(key)
    void sendClientError({
      message: ("[root] " + (error.message || "Root layout error")).slice(0, 500),
      stack: error.stack,
      digest: error.digest,
    })
  }, [error])

  return (
    <html lang="en">
      <body style={{ margin: 0, fontFamily: "system-ui, sans-serif", background: "#FDFBF7" }}>
        <div role="alert" aria-live="assertive" style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div style={{ maxWidth: 480, textAlign: "center" }}>
            <h1 style={{ fontSize: 26, fontWeight: 700, color: "#1B2D54", margin: "0 0 12px" }}>
              Something went wrong
            </h1>
            <p style={{ fontSize: 15, color: "#6b6f7a", lineHeight: 1.7, margin: "0 0 28px" }}>
              We hit an unexpected error and our team has been notified. Please try again.
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
              <button
                onClick={reset}
                style={{ padding: "12px 28px", background: "#1B2D54", color: "#FDFBF7", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: "pointer" }}
              >
                Try again
              </button>
              {/* Plain <a>, not next/link: global-error replaces the root layout
                  when the app shell itself has failed, so we must do a full
                  document navigation rather than client-side routing. */}
              {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
              <a
                href="/"
                style={{ padding: "12px 28px", background: "transparent", color: "#1B2D54", border: "1px solid #e8e4dc", borderRadius: 10, fontSize: 15, fontWeight: 600, textDecoration: "none" }}
              >
                Go to homepage
              </a>
            </div>
          </div>
        </div>
      </body>
    </html>
  )
}
